import * as CurrencyFormatter from '@Components/Utilities/CurrencyFormatter';
import { getURLParameter, isDateCurrentDay, sandhillsRequest, sandhillsLogging } from '@Components/Utilities/SandhillsUtility';
import { SignalRConnectionStatus } from '@Components/Utilities/SandhillsConstants';

const transportMethods = ['webSockets', 'longPolling'];
const statusSubscribers = [];

// Isolated SignalR setup in the event we end up having to support multiple hubs
let _auctionTimeBiddingSignalRConnection = null;
let _auctionTimeBiddingSignalRHub = null;
let _isAuctionTimeMobileApp = false;
let _currentWatchedEventIds = [];
let _userAuthID = null;

let _signalRFailedConnectionCounter = 0;

let _userIsEmployee = false;
let _dateTracker = new Date();

const setupAuctionTimeBiddingSignalRHub = (hubUrl, isEmployee, isMobileAppContext, eventIds, updateBiddingData, updateBiddingPausedStatus, userAuthID) => {    
    _isAuctionTimeMobileApp = isMobileAppContext ?? false;
    _currentWatchedEventIds = eventIds;
    
    _userIsEmployee = isEmployee;

    // Try catch around the connection creation in the event the SignalR library is not available
    // This will at least prevent the page from going completely blank
    try {
        _auctionTimeBiddingSignalRConnection = $.hubConnection();
    }
    catch (ex) { }
    if (_auctionTimeBiddingSignalRConnection == null || _auctionTimeBiddingSignalRConnection == undefined) {
        logAuctionMessage("SignalR connection failed: None available");
        return false;
    }

    _auctionTimeBiddingSignalRConnection.url = hubUrl;
    _auctionTimeBiddingSignalRHub = _auctionTimeBiddingSignalRConnection.createHubProxy("auctiontimebidding");
    if (_auctionTimeBiddingSignalRHub == null || _auctionTimeBiddingSignalRHub == undefined) {        
        logAuctionMessage("SignalR connection failed: No hub was provided");
        return false;
    }

    _userAuthID = userAuthID;

    _auctionTimeBiddingSignalRConnection.qs = { "referrerUrl": window.location.href, "userAuthID": userAuthID };
    _auctionTimeBiddingSignalRConnection.logging = isEmployee;

    _auctionTimeBiddingSignalRHub.on("jsUpdateMessage", function(auctionUpdate) {
        // Parse this into the object we'd expect back from the AuctionUpdate API for consistent data handling on the consuming side
        updateBiddingData([{
            EventIDPub: auctionUpdate.EventIDPub,
            EventID: auctionUpdate.EventID,
            NumberOfBids: auctionUpdate.NumberOfBids,
            CurrentBid: auctionUpdate.CurrentBid,
            NextBid: auctionUpdate.NextBid,
            SecondsRemaining: auctionUpdate.SecondsRemaining,
            OpeningBid: auctionUpdate.OpeningBid,
            HighBidderLowerPubTUID: auctionUpdate.HighBidderLowerPubTUID,
            HighBidderCentralUserAuthID: auctionUpdate.HighBiddercentraluserAuthID,
            BidIncrement: auctionUpdate.BidIncrement,
            AuctionUpdateCounter: auctionUpdate.AuctionUpdateCounter,
        }], null);
    });

    _auctionTimeBiddingSignalRHub.on("jsBiddingPaused", function (biddingPaused) {
        updateBiddingPausedStatus(biddingPaused)
    });

    _auctionTimeBiddingSignalRConnection.disconnected(function () {
        logAuctionMessage("Connection disconnected, Reconnecting.", " Last Error ", _auctionTimeBiddingSignalRConnection?.lastError?.message);
        
        // Push event to all subscribers
        triggerSignalRNotConnectedEventIfNeeded();

        setTimeout(function () {
            startSignalRConnection(true);               
        }, 3000); // attempt to restart connection after 3 seconds.
    });

    _auctionTimeBiddingSignalRConnection.reconnecting(function () {
        logAuctionMessage("Attempting to reconnect");        
    });

    _auctionTimeBiddingSignalRConnection.reconnected(function () {
        logAuctionMessage("Attempt to reconnect has succeeded");
    });

    _auctionTimeBiddingSignalRConnection.connectionSlow(function () {
        // Alert user or log?
        logAuctionMessage("Slow connection detected");
    });

    startSignalRConnection(false);

    return true;
};

const changeWatchedAuctionGroups = (newEventIds) => {
    const connectionStatus = getSignalRConnectionStatus();
    if (connectionStatus == SignalRConnectionStatus.Connected && _auctionTimeBiddingSignalRHub) {
        // Change out the groups we're subscribed to
        _auctionTimeBiddingSignalRHub.invoke("removeFromGroups", _currentWatchedEventIds).done(function () {
            _currentWatchedEventIds = newEventIds;
            _auctionTimeBiddingSignalRHub.invoke("addToGroups", newEventIds, false);
        });
    }
    else {
        // Just update the list, we'll pick it up on the next connection
        _currentWatchedEventIds = newEventIds;
    }
}

const startSignalRConnection = (pushLatestData) => {
    _auctionTimeBiddingSignalRConnection.start({ jsonp: true, transport: transportMethods }).done(function () {
        _auctionTimeBiddingSignalRHub.invoke("addToGroups", _currentWatchedEventIds, pushLatestData);                
        logAuctionMessage("Connection succeeded");            
    }).fail(function (e) {
        console.log("SignalR connection failed: " + e);
        _signalRFailedConnectionCounter++;
        triggerSignalRNotConnectedEventIfNeeded();
    });    
}

const extendAuctionTimeBidding = (routes, auctionBiddingData) => {
    if (auctionBiddingData?.TimeRemaining && auctionBiddingData.TimeRemaining <= 60) {
        sandhillsLogging(routes.LogBidInformation, {
            listingId: auctionBiddingData.ListingID,
            bidValue: auctionBiddingData.UserBid,
            logLocation: 'extendAuction',
            isQuickBid: 'false'
        });

        const connectionStatus = getSignalRConnectionStatus();
        const nextBid = (auctionBiddingData?.CurrentBid || 0) + auctionBiddingData.Increment;

        if (!_isAuctionTimeMobileApp && connectionStatus == SignalRConnectionStatus.Connected && _auctionTimeBiddingSignalRHub && !getURLParameter(window.location.href, "ajaxBidding")) {
            // Connected to SignalR, use that to extend the auction
            _auctionTimeBiddingSignalRHub.invoke("extendAuction", auctionBiddingData.ListingID, auctionBiddingData.LegacySiteID, auctionBiddingData.LegacyBaseCatID, nextBid, auctionBiddingData.CurrentBid).fail(function (error) {
                console.log(error);
            }).done(function (data) {
                postExtendAuctionActions(routes, auctionBiddingData, data);
            });
        }
        else {
            // AJAX fallback, currently this is neccesary for the mobile app since WS do not support passing through the bearer
            sandhillsRequest({
                url: routes.ExtendAuction,
                data: {
                    ListingID: auctionBiddingData.ListingID,
                    EventID: auctionBiddingData.EventID,
                    SiteId: auctionBiddingData.LegacySiteID,
                    BaseCategoryId: auctionBiddingData.LegacyBaseCatID,
                    UserBid: nextBid,
                    CurrentBid: auctionBiddingData.CurrentBid,
                },
                method: 'post',
                withCredentials: true,
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                postExtendAuctionActions(routes, auctionBiddingData, data);
            })
            .catch((error) => {
                console.log(error);
            });
        }
    }
}

const placeAuctionTimeBid = (routes, auctionBiddingData, isQuickBid) => {
    return new Promise((resolve, reject) => {
        sandhillsLogging(routes.LogBidInformation, {
            listingId: auctionBiddingData.ListingID,
            bidValue: auctionBiddingData.UserBid,
            logLocation: 'attemptedBid',
            isQuickBid: isQuickBid.toString(),
            isUsersFirstBid: auctionBiddingData.UserBidInfo?.UsersLastBid <= 0 ? 'true' : 'false'
        });

        const connectionStatus = getSignalRConnectionStatus();

         const bidSubmission = {
                 "BaseCategoryId": auctionBiddingData.LegacyBaseCatID,
                 "CentralUserAuthId": _userAuthID,
                 "BidSubmittedFrom": window.location.pathname,
                 "CurrencyCode": auctionBiddingData.CurrencyCode,
                 "IsQuickBid": isQuickBid,
                 "ListingID": auctionBiddingData.ListingID,
                 "EventID": auctionBiddingData.EventID,
                 "SiteID": auctionBiddingData.LegacySiteID,
                 "UsersBid": auctionBiddingData.UserBid
         };

        if (!_isAuctionTimeMobileApp && connectionStatus == SignalRConnectionStatus.Connected && _auctionTimeBiddingSignalRHub && !getURLParameter(window.location.href, "ajaxBidding")) {
            // Connected to SignalR, use that to place the bid

            _auctionTimeBiddingSignalRHub.invoke("placeBid", bidSubmission).fail(function (error) {
                postBidActions(routes, auctionBiddingData, isQuickBid, { Success: false, Message: error });
                reject(error);
            }).done(function (data) {
                data = postBidActions(routes, auctionBiddingData, isQuickBid, data);
                resolve(data);
            });
        }
        else {
            // AJAX fallback, currently this is neceesary for the mobile app since WS do not support passing through the bearer
            sandhillsRequest({
                url: routes.PlaceBid,
                data: {
                    "BidSubmission": bidSubmission,
                },
                method: 'post',
                withCredentials: true,
                errorHandling: {
                    useConsole: false,
                    useAlert: false
                }
            }).then((data) => {
                data = postBidActions(routes, auctionBiddingData, isQuickBid, data);
                resolve(data);
            })
            .catch((error) => {
                postBidActions(routes, auctionBiddingData, isQuickBid, { Success: false, Message: error });
                reject(error);
            });
        }
    });
}

// This takes our existing AuctionTimeInfo : AuctionInfo object and patches in the updates from either SignalR or the AuctionUpdates API
const patchInUpdatedAuctionData = (routes, currentAuctionBiddingData, auctionUpdateData, biddingInputPaused) => {

    const didDateChange = !isDateCurrentDay(_dateTracker);
    
    if (didDateChange == true) {
        // Date changed, so reset date tracker to today.  If the date changed, then we should accept the update and not skip it since the auction update counter would have reset for the new day.
        _dateTracker = new Date();
    }

    //Check that update is not stale.  If "AuctionUpdateCounter" from update is < current counter, then should be safe to treat this as a duplicate and ignore. Initially used <= here and incremented update counter when processing a bid when pushing update and then incremented before returning bid result.  Changed so both of these use the same update counter value now.
    if (!didDateChange && auctionUpdateData.AuctionUpdateCounter && currentAuctionBiddingData.AuctionUpdateCounter && auctionUpdateData.AuctionUpdateCounter < currentAuctionBiddingData.AuctionUpdateCounter) {
        let err = 'Ignored update due to update being out of date! Current - ' + currentAuctionBiddingData.AuctionUpdateCounter + '; update - ' + auctionUpdateData.AuctionUpdateCounter + '; current end time - ' + currentAuctionBiddingData.ProjectedEndDateTime + '; update end time - ' + new Date(Date.now() + (auctionUpdateData.SecondsRemaining * 1000)).toLocaleString(); 

        logAuctionMessage(err);

        return currentAuctionBiddingData; // update appears to be behind, so ignore it.
    }

    if (auctionUpdateData.NumberOfBids < currentAuctionBiddingData.NumberOfBids) {
        return currentAuctionBiddingData; // Until we have a timestamp, say this is old data if the number of bids goes down
    }
    // copy the object just so we're not touching the original
    const updatedData = { ...currentAuctionBiddingData };

    // SignalR does not include the overarching bidding paused status. That comes from a separate push on that end
    // AuctionsUpdate API will return both the data and the overaching bidding paused status
    if (biddingInputPaused != null) {
        updatedData.IsAllBiddingPaused = biddingInputPaused;
    }

    if (auctionUpdateData) {
        // Patch in the updated data
        updatedData.NumberOfBids = auctionUpdateData.NumberOfBids;
        updatedData.CurrentBid = auctionUpdateData.CurrentBid;
        updatedData.CurrentBidFormatted = `${CurrencyFormatter.formatBidAmount(auctionUpdateData.CurrentBid, updatedData.CurrencyCode, updatedData.CultureCode)} ${updatedData?.Quantity > 1 || !updatedData.BidPriceLabel.includes("Head") ? updatedData.BidPriceLabel : ''}`;
        updatedData.NextBidAmount = CurrencyFormatter.formatBidAmount(auctionUpdateData.NextBid, updatedData.CurrencyCode, updatedData.CultureCode);
        updatedData.Increment = auctionUpdateData.BidIncrement;
                        
        if (auctionUpdateData.SecondsRemaining >= 0) {
            updatedData.endTimeChanged = (auctionUpdateData.SecondsRemaining > updatedData.TimeRemaining); //Update indicates listing has been extended
                                                
            //Seconds remaining went up, so update the projected end time to reflect the new time remaining            
            if (updatedData.endTimeChanged) {                
                updatedData.ProjectedEndDateTime = new Date(Math.floor(Date.now() + (auctionUpdateData.SecondsRemaining * 1000)));                
            }
            updatedData.TimeLeft = auctionUpdateData.SecondsRemaining;
        }
        else {
            updatedData.TimeLeft = translator.translate('sandhills.tradepubwebsite.mvc.auctionhasended');
        }
        updatedData.TimeRemaining = auctionUpdateData.SecondsRemaining;

        // SignalR does not push the individual listing bidding paused status either
        if (auctionUpdateData.ListingIsPaused != null) {
            updatedData.IsListingPaused = auctionUpdateData.ListingIsPaused;
        }

        // See if we need to change any banners based on the new information
        if (updatedData.UserBidInfo && updatedData.UserBidInfo.UsersLastBid > 0 && auctionUpdateData.HighBidderLowerPubTUID) {
            if (updatedData.BiddingUserID == parseInt(auctionUpdateData.HighBidderLowerPubTUID)) {
                updatedData.UserBidInfo = { ...updatedData.UserBidInfo, UserIsOutBid: false, UserIsTieBidder: false, ListingBidStatusText: translator.translate('sandhills.tradepubwebsite.mvc.highbidder') };
            }
            else if (updatedData.UserBidInfo.UsersLastBid == auctionUpdateData.CurrentBid) {
                updatedData.UserBidInfo = { ...updatedData.UserBidInfo, UserIsOutBid: false, UserIsTieBidder: true, ListingBidStatusText: translator.translate('sandhills.tiebidder') };
            }
            else {
                updatedData.UserBidInfo = { ...updatedData.UserBidInfo, UserIsOutBid: true, UserIsTieBidder: false, ListingBidStatusText: translator.translate('sandhills.tradepubwebsite.mvc.outbidtext') };
            }
        }

        //Keep track of order of updates
        updatedData.AuctionUpdateCounter = auctionUpdateData.AuctionUpdateCounter;
    }

    return updatedData;
};

const subscribeToSignalRNotConnectedFallback = (callback) => {
    statusSubscribers.push(callback);    
}

const getSignalRConnectionStatus = () => {
    // Surrounded this by a try catch in the event the Connection is unavailable and we can at least return Disconnected
    try {
        return _auctionTimeBiddingSignalRConnection.state;
    }
    catch (ex) { }

    return SignalRConnectionStatus.Disconnected;
}

const postBidActions = (routes, auctionBiddingData, isQuickBid, data) => {
    if (data && data.Success && data.UserBidInfo) {
        // Attach the proper translation since this does not come back from the endpoint
        if (data.IsTieBid) {
            data.UserBidInfo = { ...data.UserBidInfo, ListingBidStatusText: translator.translate('sandhills.tiebidder') };
        }
        else if (data.AutoOutbid) {
            data.UserBidInfo = { ...data.UserBidInfo, ListingBidStatusText: translator.translate('sandhills.tradepubwebsite.mvc.outbidtext') };
        }
        else {
            data.UserBidInfo = { ...data.UserBidInfo, ListingBidStatusText: translator.translate('sandhills.tradepubwebsite.mvc.highbidder') };
        }
    }

    if (!data || !data.Success) {
        sandhillsLogging(routes.LogBidInformation, {
            listingId: auctionBiddingData.ListingID,
            bidValue: auctionBiddingData.UserBid,
            logLocation: 'failedBid',
            isQuickBid: isQuickBid.toString(),
            message: encodeURIComponent(data?.Message) || 'No response data',
        });
    }

    return data;
}

const postExtendAuctionActions = (routes, auctionBiddingData, data) => {
    if (!data || !data.Success) {
        sandhillsLogging(routes.LogBidInformation, {
            listingId: auctionBiddingData.ListingID,
            bidValue: auctionBiddingData.UserBid,
            logLocation: 'failedAuctionExtension',
            isQuickBid: 'false',
            message: encodeURIComponent(data?.Message) || 'No response data',
        });
    }
}

const logAuctionMessage = (...args) => {
    // Base this message log on whether or not logging is enabled for SignalR in general
    if (_userIsEmployee == true || _auctionTimeBiddingSignalRConnection?.logging == true) {
        console.log("[" + new Date().toTimeString() + "]", args);
    }
}

const triggerSignalRNotConnectedEventIfNeeded = () => {
    if (_signalRFailedConnectionCounter == 10) {
        //After 10 connection failures trigger SignalRConnectionIssues callback.  For now just do it once and let consuming code decide what to do. (Initialy added this to allow consuming page to start auction update pull requests if the page has listings ending on the current day.)
        statusSubscribers.forEach((callback) => callback(getSignalRConnectionStatus()));
    }
}

const getBidStatusBannerQueryString = (userBidInfoList) => {
    if (!userBidInfoList?.length) {
        return "";
    }

    const highBidderStrings = [];
    const outbidStrings = [];
    const tieBidderStrings = [];

    for (const bidInfo of userBidInfoList) {
        const listingId = bidInfo.ListingID;

        if (bidInfo.UserBidInfo?.UserIsTieBidder) {
            tieBidderStrings.push(listingId);
        }
        else if (bidInfo.UserBidInfo?.UserIsOutBid) {
            outbidStrings.push(listingId);
        }
        else if (bidInfo.UserBidInfo?.UsersLastBid > 0) {
            highBidderStrings.push(listingId);
        }
    }
    
    return [
        highBidderStrings.length && `hbb=${encodeURIComponent(highBidderStrings.join(","))}`,
        outbidStrings.length && `obb=${encodeURIComponent(outbidStrings.join(","))}`,
        tieBidderStrings.length && `tbb=${encodeURIComponent(tieBidderStrings.join(","))}`
    ].filter(Boolean).join("&");
};



export {
    changeWatchedAuctionGroups,
    extendAuctionTimeBidding,
    getSignalRConnectionStatus,
    logAuctionMessage,
    placeAuctionTimeBid,
    patchInUpdatedAuctionData,
    setupAuctionTimeBiddingSignalRHub,
    subscribeToSignalRNotConnectedFallback,
    getBidStatusBannerQueryString
};